import {
  StatusBarV4,
  StatusBarStatusColorsV4,
  StatusBarColorsV4,
} from '@labrav/react-components'
import { OfferStatus, PrivateOffer } from '../../../modules/privateOffer/types'
import { useStyles } from './MicrosoftOfferHeader.styles'
import { productDetailDate } from '../../utils/productDetailDate'
import { useTimeZone } from '../../utils/hooks/useTimeZone'
import clsx from 'clsx'
import { getExpirationFromDate } from '@labrav/common'
import { ViewError } from '../../../components/ViewError/ViewError'
import {
  OfferHeaderActions,
  type PrivateOfferViewTrackingHandlers,
} from './OfferHeaderActions/OfferHeaderActions'

export const MicrosoftOfferHeader = ({
  privateOffer,
  externalBaseURL,
  search,
  partnerId,
  viewTracking,
}: {
  privateOffer: PrivateOffer
  externalBaseURL?: string
  search?: string
  partnerId?: string
  viewTracking?: PrivateOfferViewTrackingHandlers
}) => {
  const { classes } = useStyles()
  const { timeZone } = useTimeZone()

  const { offerStatus, updatedAt, acceptBy, createdAt } = privateOffer
  const statusView = getMicrosoftStatus(offerStatus)

  const { modifiedDate: offerSentAtDate, modifiedTime: offerSentAtTime } =
    privateOffer?.auditJson?.offerSentAt &&
    privateOffer.auditJson.offerSentAt !== null
      ? productDetailDate(privateOffer.auditJson.offerSentAt, timeZone)
      : { modifiedDate: '', modifiedTime: '' }

  const { fullDateTime: offerCreationFailedDate } =
    (privateOffer?.errorDetails?.errors || privateOffer?.error) &&
    (updatedAt || createdAt)
      ? productDetailDate(updatedAt || createdAt, timeZone)
      : { fullDateTime: '' }

  const { commaSeparatedDateTime: offerAcceptedDate } =
    privateOffer?.auditJson?.offerAcceptedAt &&
    privateOffer.auditJson.offerAcceptedAt !== null
      ? productDetailDate(privateOffer.auditJson.offerAcceptedAt, timeZone)
      : { commaSeparatedDateTime: '' }

  const { modifiedDate: offerExpiredDate } =
    privateOffer?.offerExpirationDate &&
    privateOffer.offerExpirationDate !== null
      ? productDetailDate(privateOffer.offerExpirationDate, timeZone)
      : { modifiedDate: '' }

  const { modifiedDate: offerRestricted } =
    privateOffer?.statusLogs?.offerRestricted &&
    privateOffer?.statusLogs?.offerRestricted !== null
      ? productDetailDate(privateOffer?.statusLogs?.offerRestricted, timeZone)
      : { modifiedDate: '' }

  const { commaSeparatedDateTime: acceptByDate } =
    acceptBy && acceptBy !== null
      ? productDetailDate(acceptBy, timeZone)
      : { commaSeparatedDateTime: '' }

  const { isExpriringSoon, dayDiff, isOneDayLeft } = getExpirationFromDate(
    acceptBy || '',
    5
  )

  const offerExpiringSoon =
    isExpriringSoon &&
    [OfferStatus.OFFER_EMAIL_SENT, OfferStatus.OFFER_VIEWED].includes(
      offerStatus
    )

  const noStatusBar =
    offerStatus &&
    !offerExpiringSoon &&
    [OfferStatus.PRIVATE_OFFER_EMAIL_SENT].includes(offerStatus)

  return (
    <>
      <div className={clsx(classes.status, noStatusBar && classes.noStatusBar)}>
        {offerStatus === OfferStatus.OFFER_CREATION_FAILED && (
          <StatusBarV4
            backgroundColor={StatusBarColorsV4.RED}
            status={statusView}
            divider
            statusColor={StatusBarStatusColorsV4.RED}
            content={
              privateOffer?.errorDetails?.errors || privateOffer?.error ? (
                <div
                  className={classes.errorSubContent}
                  data-testid={'failed-offer-content'}
                >
                  <div>
                    <ViewError
                      errors={privateOffer?.errorDetails?.errors || []}
                    />
                  </div>
                  <div className={classes.offerUpdatedAt}>
                    {offerCreationFailedDate}
                  </div>
                </div>
              ) : (
                ''
              )
            }
          />
        )}
        {!offerExpiringSoon && offerStatus === OfferStatus.OFFER_ACCEPTED && (
          <StatusBarV4
            backgroundColor={StatusBarColorsV4.GREEN}
            status={statusView}
            divider
            statusColor={StatusBarStatusColorsV4.GREEN}
            content={`Offer was accepted on ${offerAcceptedDate} and all steps post acceptance are completed.`}
          />
        )}
        {!offerExpiringSoon && offerStatus === OfferStatus.OFFER_VIEWED && (
          <StatusBarV4
            backgroundColor={StatusBarColorsV4.LIGHTBLUE}
            status={statusView}
            statusColor={StatusBarStatusColorsV4.LIGHTBLUE}
          />
        )}
        {!offerExpiringSoon && offerStatus === OfferStatus.OFFER_EMAIL_SENT && (
          <StatusBarV4
            backgroundColor={StatusBarColorsV4.LIGHTBLUE}
            status={statusView}
            divider
            statusColor={StatusBarStatusColorsV4.LIGHTBLUE}
            content={`Offer was sent on ${offerSentAtDate} ${offerSentAtTime}.`}
          />
        )}

        {offerStatus === OfferStatus.OFFER_RESTRICTION_IN_PROGRESS && (
          <StatusBarV4
            backgroundColor={StatusBarColorsV4.YELLOW}
            status={statusView}
            statusColor={StatusBarStatusColorsV4.YELLOW}
            divider
            content={'Deactivation is initiated, will be deactivated in 5 min.'}
          />
        )}
        {offerStatus === OfferStatus.OFFER_RESTRICTED && (
          <StatusBarV4
            backgroundColor={StatusBarColorsV4.RED}
            status={statusView}
            divider
            statusColor={StatusBarStatusColorsV4.RED}
            content={`Deactivated on ${offerRestricted}.`}
          />
        )}
        {offerStatus === OfferStatus.OFFER_EXPIRED && (
          <StatusBarV4
            backgroundColor={StatusBarColorsV4.RED}
            status={statusView}
            divider
            statusColor={StatusBarStatusColorsV4.RED}
            content={`Offer expired on ${offerExpiredDate}.`}
          />
        )}
        {offerStatus === OfferStatus.OFFER_IN_DRAFT && (
          <StatusBarV4
            backgroundColor={StatusBarColorsV4.GRAY}
            status={statusView}
            statusColor={StatusBarStatusColorsV4.GRAY}
          />
        )}
        {offerExpiringSoon && (
          <StatusBarV4
            backgroundColor={StatusBarColorsV4.YELLOW}
            status={'Expiring soon'}
            divider
            statusColor={StatusBarStatusColorsV4.YELLOW}
            content={
              isOneDayLeft
                ? 'Offer expires today.'
                : `Offer expiring in ${dayDiff} days.
                Expires at ${acceptByDate}.`
            }
          />
        )}
        {!offerExpiringSoon &&
          offerStatus === OfferStatus.OFFER_CREATION_IN_PROGRESS && (
            <StatusBarV4
              backgroundColor={StatusBarColorsV4.LIGHTBLUE}
              status={statusView}
              divider
              statusColor={StatusBarStatusColorsV4.LIGHTBLUE}
              content={`Offer is being created in the AZURE Marketplace and will be ready in approximately 5 minutes. Please refresh the page periodically to check the status.`}
            />
          )}
      </div>
      <OfferHeaderActions
        privateOffer={privateOffer}
        cloudMarketplace={'AZURE'}
        externalBaseURL={externalBaseURL}
        search={search}
        partnerId={partnerId}
        viewTracking={viewTracking}
      />
    </>
  )
}

const getMicrosoftStatus = (status?: OfferStatus) => {
  switch (status) {
    case OfferStatus.OFFER_CREATION_FAILED:
      return 'Failed'
    case OfferStatus.OFFER_ACCEPTED:
      return 'Accepted'
    case OfferStatus.OFFER_EMAIL_SENT:
      return 'Sent'
    case OfferStatus.OFFER_VIEWED:
      return 'Viewed'
    case OfferStatus.OFFER_RESTRICTION_IN_PROGRESS:
      return 'Deactivation in progress'
    case OfferStatus.OFFER_RESTRICTED:
      return 'Deactivated'
    case OfferStatus.OFFER_EXPIRED:
      return 'Expired'
    case OfferStatus.OFFER_IN_DRAFT:
      return 'Draft'
    case OfferStatus.OFFER_CREATION_IN_PROGRESS:
      return 'Creation in progress'
    default:
      return 'Unknown'
  }
}
